import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
const Subscriptions = () => {
  const user = authService.getCurrentUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const { t, locale } = useTranslation();
  const isEmployeeRole = (user?.user_company_role || user?.company_role || user?.roleInCompany) === 'employee';

  const loadSubscriptions = async (status = '') => {
    try {
      setLoading(true);
      const res = await companyService.getSubscriptions(status ? { status } : {});
      // Handle possible response shapes (array or wrapped)
      const list = Array.isArray(res) ? res : (res?.data ?? []);
      setSubscriptions(Array.isArray(list) ? list : []);
    } catch (error) {
      const message = error?.response?.data?.message || 'فشل تحميل الاشتراكات';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEmployeeRole) {
      navigate('/company/vacancies', { replace: true });
      return;
    }
    loadSubscriptions(filterStatus);
  }, [filterStatus]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ar', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const canCancel = (sub) => {
    return sub?.status === 'active';
  };

  const handleCancel = async (subscriptionId) => {
    try {
      setCancellingId(subscriptionId);
      await companyService.cancelSubscription(subscriptionId);
      toast.success(t('company.subscriptions.cancelSuccess'));
      await loadSubscriptions(filterStatus);
    } catch (error) {
      const message = error?.response?.data?.message || t('company.subscriptions.cancelFailed');
      toast.error(message);
    } finally {
      setCancellingId(null);
    }
  };

  if (loading) {
    return <Loader fullScreen={true} size="large" />;
  }

  return (
    <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <CompanyNav user={user} />

      {/* Hero Header */}
      <div className="px-3 mb-3 mt-3">
        <div className="edit-hero">
          <div className="edit-hero-inner">
            <div className="edit-hero-badge">📦</div>
            <div className="edit-hero-text flex-grow-1">
              <div className="edit-hero-title">{t('company.subscriptions.title')}</div>
              <div className="edit-hero-subtitle">{t('company.subscriptions.subtitle')}</div>
            </div>
            <div>
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ minWidth: 180 }}
              >
                <option value="">{t('company.subscriptions.allSubscriptions')}</option>
                <option value="active">{t('company.subscriptions.active')}</option>
                <option value="canceled">{t('company.subscriptions.canceled')}</option>
                <option value="expired">{t('company.subscriptions.expired')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="all-jobs-section px-3 mb-5">
        {(!subscriptions || subscriptions.length === 0) ? (
          <div className="text-center py-5">
                <p className="empty-state-text mb-3">{t('company.subscriptions.noSubscriptions')}</p>
          </div>
        ) : (
          <div className="table-responsive" style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #eef2f7' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('company.subscriptions.plan')}</th>
                  <th style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('company.subscriptions.price')}</th>
                  <th style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('company.subscriptions.status')}</th>
                  <th style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('company.subscriptions.startDate')}</th>
                  <th style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('company.subscriptions.endDate')}</th>
                  <th style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('company.subscriptions.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((sub, idx) => (
                  <tr key={sub.id} style={{ borderBottom: '1px solid #eef2f7', background: idx % 2 ? '#fff' : '#fcfcfd' }}>
                    <td style={{ padding: '12px', fontWeight: 700 }}>
                      <Link to={`/company/subscriptions/${sub.id}`} style={{ textDecoration: 'none', color: '#1f2937' }}>
                        {sub?.plan?.name || `${t('company.subscriptions.subscription')} #${sub.id}`}
                      </Link>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {sub?.plan?.price != null ? `${Number(sub.plan.price).toFixed(2)} $` : '—'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span className={`status-badge ${sub.status === 'active' ? 'status-badge-accepted' : (sub.status === 'canceled' ? 'status-badge-rejected' : 'status-badge-warning')}`}>
                        {sub.status === 'active' ? t('company.subscriptions.active') : sub.status === 'canceled' ? t('company.subscriptions.canceled') : t('company.subscriptions.expired')}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{formatDate(sub.start_date)}</td>
                    <td style={{ padding: '12px' }}>{formatDate(sub.end_date)}</td>
                    <td style={{ padding: '12px' }}>
                      {canCancel(sub) ? (
                        <button
                          onClick={() => handleCancel(sub.id)}
                          className="btn-withdraw"
                          disabled={!!cancellingId}
                        >
                          {cancellingId === sub.id ? t('company.subscriptions.cancelling') : t('company.subscriptions.cancelSubscription')}
                        </button>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Local styles */}
      <style>
        {`
          .edit-hero {
            background: linear-gradient(135deg, rgba(96,143,211,0.15) 0%, rgba(74,123,196,0.15) 100%);
            border-radius: 18px;
            padding: 14px 16px;
          }
          .edit-hero-inner {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .edit-hero-badge {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: grid;
            place-items: center;
            background: linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%);
            color: #fff;
            font-size: 22px;
            box-shadow: 0 10px 20px rgba(76, 116, 179, 0.25);
          }
          .edit-hero-title {
            font-weight: 800;
            font-size: 1.1rem;
          }
          .edit-hero-subtitle {
            color: #475569;
            font-size: 0.9rem;
          }
        `}
      </style>

      <CompanyBottomNavigation />
    </div>
  );
};

export default Subscriptions;


